import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  Search, 
  Filter, 
  Plus, 
  Trash2, 
  Edit3, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Sparkles, 
  Download, 
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Building,
  Mail,
  Quote,
  Loader2,
  X
} from 'lucide-react';
import { 
  subscribeToReviews, 
  updateReviewAdmin, 
  deleteReviewAdmin, 
  submitPublicReview, 
  seedReviewsToFirestore,
  ReviewItem 
} from '../../services/reviewService';

export const AdminReviews: React.FC = () => {
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending' | 'rejected' | 'featured'>('all');
  const [ratingFilter, setRatingFilter] = useState<number | 'all'>('all');
  
  // Modals
  const [editingReview, setEditingReview] = useState<ReviewItem | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Form state for add/edit
  const [formAuthor, setFormAuthor] = useState('');
  const [formRole, setFormRole] = useState('');
  const [formCompany, setFormCompany] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formRating, setFormRating] = useState(5);
  const [formQuote, setFormQuote] = useState('');
  const [formProjectType, setFormProjectType] = useState('Web & Mobile Apps');
  const [formStatus, setFormStatus] = useState<'approved' | 'pending' | 'rejected'>('approved');
  const [formFeatured, setFormFeatured] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    // Admin subscribes to ALL reviews (including pending/rejected)
    const unsubscribe = subscribeToReviews((data) => {
      setReviews(data);
      setLoading(false);
    }, false);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const showNotification = (text: string, type: 'success' | 'error' = 'success') => {
    setFeedbackMsg({ text, type });
    setTimeout(() => setFeedbackMsg(null), 3500);
  };

  const handleStatusChange = async (review: ReviewItem, newStatus: 'approved' | 'pending' | 'rejected') => {
    try {
      await updateReviewAdmin(review.id, { status: newStatus });
      showNotification(`Review status updated to ${newStatus}`);
    } catch (e: any) {
      showNotification(e?.message || 'Failed to update review status', 'error');
    }
  };

  const handleToggleFeatured = async (review: ReviewItem) => {
    try {
      const nextFeatured = !review.featured;
      await updateReviewAdmin(review.id, { featured: nextFeatured });
      showNotification(nextFeatured ? 'Marked as featured review' : 'Unmarked from featured');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to update featured flag', 'error');
    }
  };

  const handleDelete = async (review: ReviewItem) => {
    if (!window.confirm(`Are you sure you want to permanently delete the review by "${review.author}"?`)) {
      return;
    }
    try {
      await deleteReviewAdmin(review.id);
      showNotification('Review deleted successfully');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to delete review', 'error');
    }
  };

  const openEditModal = (review: ReviewItem) => {
    setEditingReview(review);
    setFormAuthor(review.author);
    setFormRole(review.role);
    setFormCompany(review.company || '');
    setFormEmail(review.email || '');
    setFormRating(review.rating);
    setFormQuote(review.quote);
    setFormProjectType(review.projectType || 'Web & Mobile Apps');
    setFormStatus(review.status);
    setFormFeatured(review.featured || false);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingReview) return;
    setFormSubmitting(true);
    try {
      await updateReviewAdmin(editingReview.id, {
        author: formAuthor.trim(),
        role: formRole.trim(),
        company: formCompany.trim(),
        email: formEmail.trim(),
        rating: formRating,
        quote: formQuote.trim(),
        projectType: formProjectType,
        status: formStatus,
        featured: formFeatured
      });
      setFormSubmitting(false);
      setEditingReview(null);
      showNotification('Review changes saved successfully');
    } catch (e: any) {
      setFormSubmitting(false);
      showNotification(e?.message || 'Failed to save changes', 'error');
    }
  };

  const handleCreateNew = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    try {
      await submitPublicReview({
        author: formAuthor.trim(),
        role: formRole.trim() || 'Client',
        company: formCompany.trim(),
        email: formEmail.trim(),
        quote: formQuote.trim(),
        rating: formRating,
        projectType: formProjectType
      });
      setFormSubmitting(false);
      setIsAddModalOpen(false);
      // Reset
      setFormAuthor('');
      setFormRole('');
      setFormCompany('');
      setFormEmail('');
      setFormQuote('');
      setFormRating(5);
      showNotification('New review added to public database');
    } catch (e: any) {
      setFormSubmitting(false);
      showNotification(e?.message || 'Failed to create review', 'error');
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Populate database with default verified client reviews?')) return;
    setIsSeeding(true);
    try {
      const count = await seedReviewsToFirestore();
      setIsSeeding(false);
      showNotification(`Seeded ${count} verified testimonials into Firestore`);
    } catch (e: any) {
      setIsSeeding(false);
      showNotification('Failed to seed reviews: ' + e?.message, 'error');
    }
  };

  const exportReviews = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(reviews, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `reviews-export-${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    showNotification('Exported reviews as JSON file');
  };

  // Metrics calculations
  const totalReviews = reviews.length;
  const approvedReviews = reviews.filter(r => r.status === 'approved').length;
  const pendingReviews = reviews.filter(r => r.status === 'pending').length;
  const featuredReviews = reviews.filter(r => r.featured).length;
  const avgRating = totalReviews > 0 
    ? (reviews.reduce((acc, c) => acc + c.rating, 0) / totalReviews).toFixed(1)
    : '5.0';

  // Filtering
  const filtered = reviews.filter((r) => {
    const matchesSearch = 
      r.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.quote.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.company && r.company.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = 
      statusFilter === 'all' ? true :
      statusFilter === 'featured' ? r.featured :
      r.status === statusFilter;

    const matchesRating = ratingFilter === 'all' ? true : r.rating === ratingFilter;

    return matchesSearch && matchesStatus && matchesRating;
  });

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8">
      {/* Top Banner / Toast */}
      <AnimatePresence>
        {feedbackMsg && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 border text-sm font-medium ${
              feedbackMsg.type === 'success'
                ? 'bg-emerald-950/90 text-emerald-200 border-emerald-500/40'
                : 'bg-red-950/90 text-red-200 border-red-500/40'
            }`}
          >
            {feedbackMsg.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400" />
            )}
            <span>{feedbackMsg.text}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d8aa68]/10 border border-[#d8aa68]/30 text-[#d8aa68] text-xs font-mono uppercase tracking-wider mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Public Data Management</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight font-display">
            Client Reviews &amp; Feedback Management
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Moderate, approve, feature, and manage all public reviews submitted by clients and collaborators.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            onClick={() => {
              setFormAuthor('');
              setFormRole('');
              setFormCompany('');
              setFormEmail('');
              setFormQuote('');
              setFormRating(5);
              setIsAddModalOpen(true);
            }}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#d8aa68] to-[#b37a32] text-black font-semibold text-sm flex items-center gap-2 shadow-md hover:opacity-95 transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Add Review</span>
          </button>

          <button
            onClick={handleSeedDefaults}
            disabled={isSeeding}
            className="px-3.5 py-2.5 rounded-xl bg-white/[0.05] hover:bg-white/10 border border-white/10 text-gray-300 hover:text-white text-sm flex items-center gap-2 transition-all cursor-pointer"
            title="Seed initial 5-star testimonials into Firestore"
          >
            <RefreshCw className={`w-4 h-4 ${isSeeding ? 'animate-spin' : ''}`} />
            <span>Sync Defaults</span>
          </button>

          <button
            onClick={exportReviews}
            className="px-3.5 py-2.5 rounded-xl bg-white/[0.05] hover:bg-white/10 border border-white/10 text-gray-300 hover:text-white text-sm flex items-center gap-2 transition-all cursor-pointer"
            title="Export reviews as JSON"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="p-5 rounded-2xl bg-[#0c0c0c] border border-white/10">
          <span className="text-xs text-gray-400 font-mono uppercase">Total Reviews</span>
          <div className="text-2xl font-bold text-white mt-1 font-display">{totalReviews}</div>
          <span className="text-[11px] text-gray-500">Public &amp; submitted</span>
        </div>

        <div className="p-5 rounded-2xl bg-[#0c0c0c] border border-white/10">
          <span className="text-xs text-gray-400 font-mono uppercase">Average Rating</span>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-2xl font-bold text-[#d8aa68] font-display">{avgRating}</span>
            <div className="flex text-[#d8aa68]">
              <Star className="w-4 h-4 fill-current" />
            </div>
          </div>
          <span className="text-[11px] text-gray-500">Out of 5.0 stars</span>
        </div>

        <div className="p-5 rounded-2xl bg-[#0c0c0c] border border-white/10">
          <span className="text-xs text-gray-400 font-mono uppercase">Approved &amp; Live</span>
          <div className="text-2xl font-bold text-emerald-400 mt-1 font-display">{approvedReviews}</div>
          <span className="text-[11px] text-emerald-400/80">Visible on homepage</span>
        </div>

        <div className="p-5 rounded-2xl bg-[#0c0c0c] border border-white/10">
          <span className="text-xs text-gray-400 font-mono uppercase">Pending Review</span>
          <div className="text-2xl font-bold text-amber-400 mt-1 font-display">{pendingReviews}</div>
          <span className="text-[11px] text-gray-500">Awaiting moderation</span>
        </div>

        <div className="p-5 rounded-2xl bg-[#0c0c0c] border border-white/10">
          <span className="text-xs text-gray-400 font-mono uppercase">Featured Badged</span>
          <div className="text-2xl font-bold text-purple-400 mt-1 font-display">{featuredReviews}</div>
          <span className="text-[11px] text-gray-500">Highlighted cards</span>
        </div>
      </div>

      {/* Filter and Search Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-[#0c0c0c] border border-white/10">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search author, company, quote..."
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d8aa68]"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          {/* Status filter */}
          <div className="flex items-center gap-1 bg-white/[0.04] p-1 rounded-xl border border-white/10 text-xs">
            {(['all', 'approved', 'pending', 'featured'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg capitalize transition-colors ${
                  statusFilter === status
                    ? 'bg-[#d8aa68] text-black font-semibold'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Rating filter */}
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="px-3 py-2 rounded-xl bg-[#141414] border border-white/10 text-white text-xs focus:outline-none focus:border-[#d8aa68]"
          >
            <option value="all">All Stars</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>
      </div>

      {/* Reviews Table / Card List */}
      {loading ? (
        <div className="p-16 text-center text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-[#d8aa68]" />
          <p className="text-sm font-mono">Loading reviews from database...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-16 text-center rounded-2xl bg-[#0c0c0c] border border-white/10">
          <Quote className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-white mb-1">No reviews match your filter</h3>
          <p className="text-sm text-gray-400 max-w-md mx-auto mb-4">
            Try adjusting your search criteria or click "Sync Defaults" to restore initial client feedback.
          </p>
          <button
            onClick={() => { setSearchTerm(''); setStatusFilter('all'); setRatingFilter('all'); }}
            className="text-xs text-[#d8aa68] underline"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((item) => (
            <motion.div
              key={item.id}
              layout
              className="p-5 sm:p-6 rounded-2xl bg-[#0c0c0c] border border-white/10 hover:border-white/20 transition-all shadow-md flex flex-col md:flex-row md:items-start justify-between gap-5"
            >
              <div className="space-y-3 flex-1">
                {/* Meta row: Stars, Status badge, Featured tag, Project type */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex text-[#d8aa68]">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 ${i < item.rating ? 'fill-current' : 'text-gray-700'}`}
                      />
                    ))}
                  </div>

                  <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-mono font-medium ${
                    item.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                    item.status === 'pending' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                    'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}>
                    {item.status.toUpperCase()}
                  </span>

                  {item.featured && (
                    <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-purple-500/10 text-purple-300 border border-purple-500/20 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      <span>Featured</span>
                    </span>
                  )}

                  {item.projectType && (
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-white/[0.04] text-gray-300 border border-white/5">
                      {item.projectType}
                    </span>
                  )}

                  <span className="text-[11px] font-mono text-gray-500 ml-auto">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </span>
                </div>

                {/* Review Text */}
                <p className="text-gray-200 text-sm sm:text-base leading-relaxed italic">
                  "{item.quote}"
                </p>

                {/* Author Info */}
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 pt-1">
                  <span className="font-semibold text-white">{item.author}</span>
                  <span>{item.role} {item.company ? `at ${item.company}` : ''}</span>
                  {item.email && (
                    <span className="flex items-center gap-1 text-gray-500">
                      <Mail className="w-3 h-3" />
                      <span>{item.email}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Action Toolbar */}
              <div className="flex md:flex-col items-center gap-2 pt-3 md:pt-0 border-t md:border-t-0 border-white/5 shrink-0">
                {/* Toggle status buttons */}
                <div className="flex items-center gap-1">
                  {item.status !== 'approved' && (
                    <button
                      onClick={() => handleStatusChange(item, 'approved')}
                      className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-medium transition-all"
                      title="Approve review"
                    >
                      Approve
                    </button>
                  )}
                  {item.status !== 'pending' && (
                    <button
                      onClick={() => handleStatusChange(item, 'pending')}
                      className="px-2.5 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-medium transition-all"
                      title="Set to pending"
                    >
                      Pending
                    </button>
                  )}
                  {item.status !== 'rejected' && (
                    <button
                      onClick={() => handleStatusChange(item, 'rejected')}
                      className="px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-medium transition-all"
                      title="Reject review"
                    >
                      Reject
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {/* Feature toggle */}
                  <button
                    onClick={() => handleToggleFeatured(item)}
                    className={`p-2 rounded-lg border text-xs transition-all ${
                      item.featured
                        ? 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                        : 'bg-white/[0.04] text-gray-400 hover:text-white border-white/10'
                    }`}
                    title={item.featured ? 'Remove from featured' : 'Feature on homepage'}
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>

                  {/* Edit button */}
                  <button
                    onClick={() => openEditModal(item)}
                    className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 text-xs transition-all"
                    title="Edit review"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-2 rounded-lg bg-white/[0.04] hover:bg-red-500/20 text-gray-400 hover:text-red-400 border border-white/10 text-xs transition-all"
                    title="Delete review"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* EDIT MODAL */}
      <AnimatePresence>
        {editingReview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#111111] border border-white/15 rounded-3xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={() => setEditingReview(null)}
                className="absolute top-5 right-5 text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/5 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-xl font-bold text-white mb-4 font-display">
                Edit Review
              </h3>

              <form onSubmit={handleSaveEdit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Author Name</label>
                    <input
                      type="text"
                      required
                      value={formAuthor}
                      onChange={(e) => setFormAuthor(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Role</label>
                    <input
                      type="text"
                      value={formRole}
                      onChange={(e) => setFormRole(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Company</label>
                    <input
                      type="text"
                      value={formCompany}
                      onChange={(e) => setFormCompany(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Rating</label>
                    <select
                      value={formRating}
                      onChange={(e) => setFormRating(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl bg-[#141414] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    >
                      <option value={5}>5 Stars (Exceptional)</option>
                      <option value={4}>4 Stars (Good)</option>
                      <option value={3}>3 Stars (Average)</option>
                      <option value={2}>2 Stars (Poor)</option>
                      <option value={1}>1 Star (Terrible)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-gray-400 mb-1">Project Type</label>
                  <input
                    type="text"
                    value={formProjectType}
                    onChange={(e) => setFormProjectType(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono text-gray-400 mb-1">Review Quote</label>
                  <textarea
                    rows={4}
                    value={formQuote}
                    onChange={(e) => setFormQuote(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Status</label>
                    <select
                      value={formStatus}
                      onChange={(e) => setFormStatus(e.target.value as any)}
                      className="w-full px-3 py-2 rounded-xl bg-[#141414] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    >
                      <option value="approved">Approved (Live)</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="flex items-center pt-5">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={formFeatured}
                        onChange={(e) => setFormFeatured(e.target.checked)}
                        className="rounded border-gray-600 text-[#d8aa68] focus:ring-[#d8aa68]"
                      />
                      <span>Featured on Homepage</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setEditingReview(null)}
                    className="px-4 py-2 rounded-xl text-gray-400 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="px-5 py-2 rounded-xl bg-[#d8aa68] text-black font-semibold text-sm hover:opacity-95 transition-opacity"
                  >
                    {formSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ADD MODAL */}
      <AnimatePresence>
        {isAddModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#111111] border border-white/15 rounded-3xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="absolute top-5 right-5 text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/5 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-xl font-bold text-white mb-4 font-display">
                Add Review / Endorsement
              </h3>

              <form onSubmit={handleCreateNew} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Author Name *</label>
                    <input
                      type="text"
                      required
                      value={formAuthor}
                      onChange={(e) => setFormAuthor(e.target.value)}
                      placeholder="e.g. Alex Rivera"
                      className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Role</label>
                    <input
                      type="text"
                      value={formRole}
                      onChange={(e) => setFormRole(e.target.value)}
                      placeholder="e.g. Founder & CEO"
                      className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Company</label>
                    <input
                      type="text"
                      value={formCompany}
                      onChange={(e) => setFormCompany(e.target.value)}
                      placeholder="e.g. Stratos Inc."
                      className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-400 mb-1">Rating</label>
                    <select
                      value={formRating}
                      onChange={(e) => setFormRating(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl bg-[#141414] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                    >
                      <option value={5}>5 Stars (Exceptional)</option>
                      <option value={4}>4 Stars (Good)</option>
                      <option value={3}>3 Stars (Average)</option>
                      <option value={2}>2 Stars (Poor)</option>
                      <option value={1}>1 Star (Terrible)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-gray-400 mb-1">Project Solution</label>
                  <select
                    value={formProjectType}
                    onChange={(e) => setFormProjectType(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-[#141414] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                  >
                    <option value="Web & Mobile Apps">Custom Web &amp; Mobile Apps</option>
                    <option value="Admin Dashboards & Portals">Admin Dashboards &amp; Operations Portals</option>
                    <option value="Login Portals & Security">Secure Login Portals &amp; IAM</option>
                    <option value="Cybersecurity & RBAC">Cybersecurity &amp; Access Control</option>
                    <option value="Full-Stack System Architecture">Full-Stack System Architecture</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-gray-400 mb-1">Review Quote *</label>
                  <textarea
                    rows={4}
                    required
                    value={formQuote}
                    onChange={(e) => setFormQuote(e.target.value)}
                    placeholder="Enter review testimonial text..."
                    className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-sm focus:outline-none focus:border-[#d8aa68]"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="px-4 py-2 rounded-xl text-gray-400 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="px-5 py-2 rounded-xl bg-[#d8aa68] text-black font-semibold text-sm hover:opacity-95 transition-opacity"
                  >
                    {formSubmitting ? 'Saving...' : 'Add Review'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
